/**
 * What the visitor was about to do when they were asked to sign in.
 *
 * Signing in is a round trip through another origin, and the visitor comes back
 * to a fresh page that remembers nothing. Carrying the intent in the URL is
 * what makes them land in the room they were heading for rather than back on
 * the entry screen, having forgotten why they left it.
 *
 * The URL says `?enter=…` on the way back. A landing URL is handed to an
 * identity provider, so it says plainly what it will do when it is opened.
 */

export interface EntryIntent {
  kind: "create" | "join";
  /** The room being joined; a creation has no code yet. */
  code?: string;
}

const ENTER_PARAM = "enter";
const ROOM_PARAM = "room";

const parse = (search: string, key: string): EntryIntent | null => {
  const params = new URLSearchParams(search);
  const kind = params.get(key);
  const code = params.get(ROOM_PARAM)?.trim().toLowerCase() || undefined;
  if (kind === "create") return { kind: "create" };
  // A join without a code has nowhere to go, so it is not an intent at all.
  if (kind === "join" && code) return { kind: "join", code };
  return null;
};

/** Where a sign-in round trip comes back to, carrying an intent home. */
export function landingUrlFor(origin: string, intent: EntryIntent): string {
  const params = new URLSearchParams({ [ENTER_PARAM]: intent.kind });
  if (intent.code) params.set(ROOM_PARAM, intent.code);
  return `${origin}/?${params.toString()}`;
}

/** The intent a landing URL carries, read from a search string. */
export function intentFromLanding(search: string): EntryIntent | null {
  return parse(search, ENTER_PARAM);
}

/**
 * Rubs the intent out of the address, wherever it landed.
 *
 * The round trip comes home to `/`, which sends the visitor on to the address
 * that exists today and carries the query with it (#610) — everything in it,
 * because the client reads the identity provider's answer out of the address
 * after the routing has already happened. What this application put there is
 * spent by the time that page renders: the room is in the path and the intent
 * did its work by choosing it, so both are removed rather than left to be read
 * again by a reload.
 */
export function clearEntryIntentFromAddress({
  /** The way in keeps it: `?room=` there is a code in hand, not an intent. */
  keepRoom = false,
}: { keepRoom?: boolean } = {}): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const spent =
    url.searchParams.has(ENTER_PARAM) ||
    (!keepRoom && url.searchParams.has(ROOM_PARAM));
  if (!spent) return;
  url.searchParams.delete(ENTER_PARAM);
  if (!keepRoom) url.searchParams.delete(ROOM_PARAM);
  window.history.replaceState({}, "", url.toString());
}

/**
 * Reads the intent the current URL carries and rubs it out.
 *
 * Rubbed out because it is a one-shot instruction: a reload of the same address
 * must not walk back into a room the visitor has since left.
 */
export function takeEntryIntent(): EntryIntent | null {
  if (typeof window === "undefined") return null;
  const intent = intentFromLanding(window.location.search);
  if (!intent) return null;
  const url = new URL(window.location.href);
  url.searchParams.delete(ENTER_PARAM);
  window.history.replaceState({}, "", url.toString());
  return intent;
}

/**
 * The intent a landing URL carries, read from parsed search params.
 *
 * The router hands the first render its search, which is the only reading of
 * the address available identically on the server and in the browser — and so
 * the only one the very first frame can be decided by.
 */
export function intentFromLandingParams(params: {
  enter?: string;
  room?: string;
}): EntryIntent | null {
  const search = new URLSearchParams();
  if (params.enter) search.set(ENTER_PARAM, params.enter);
  if (params.room) search.set(ROOM_PARAM, params.room);
  return intentFromLanding(search.toString());
}
